import { useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ reveal */
/**
 * Fades content up as it scrolls into view. Uses one observer per element and
 * disconnects on first intersection — cheap, and it never re-hides on scroll-up.
 */
export function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? 'is-in' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* --------------------------------------------------------------- ornaments */
/** Gold rule with a centred lozenge — the divider used throughout the site. */
export function Ornament({ className = '', tone = 'dark' }) {
  const line = tone === 'dark' ? 'via-gold-500/55' : 'via-gold-300/60';
  const gem = tone === 'dark' ? 'bg-gold-500' : 'bg-gold-300';
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden="true">
      <span className={`h-px w-14 bg-gradient-to-r from-transparent ${line} to-transparent`} />
      <span className={`h-1.5 w-1.5 rotate-45 ${gem}`} />
      <span className={`h-px w-14 bg-gradient-to-r from-transparent ${line} to-transparent`} />
    </div>
  );
}

/** Section heading: eyebrow, display title, optional lede. */
export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = 'center',
  tone = 'dark',
  className = '',
}) {
  const centred = align === 'center';
  return (
    <div
      className={`${centred ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'} ${className}`}
    >
      {eyebrow && (
        <p className={tone === 'dark' ? 'eyebrow' : 'eyebrow-light'}>{eyebrow}</p>
      )}
      <h2
        className={`display-lg mt-4 ${tone === 'dark' ? 'text-royal-900' : 'text-cream-50'}`}
      >
        {title}
      </h2>
      <Ornament tone={tone} className={`mt-6 ${centred ? '' : '!justify-start'}`} />
      {lede && (
        <p
          className={`mt-6 ${
            tone === 'dark' ? 'lede' : 'text-[15px] leading-relaxed text-cream-200/75 sm:text-base'
          }`}
        >
          {lede}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- icons */
export const Icon = {
  Phone: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <path
        d="M6.6 3h2.2l1.5 4-1.9 1.4a12 12 0 0 0 5.2 5.2l1.4-1.9 4 1.5v2.2A2.6 2.6 0 0 1 16.4 18C10.1 17.6 6.4 13.9 6 7.6A2.6 2.6 0 0 1 6.6 3Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Mail: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="m3.6 6.2 8.4 6.3 8.4-6.3" strokeLinecap="round" />
    </svg>
  ),
  Pin: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <path d="M12 21c4.2-4.6 6.3-7.8 6.3-10.4A6.3 6.3 0 0 0 5.7 10.6C5.7 13.2 7.8 16.4 12 21Z" />
      <circle cx="12" cy="10.3" r="2.4" />
    </svg>
  ),
  Clock: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.6V12l3 1.9" strokeLinecap="round" />
    </svg>
  ),
  Instagram: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <rect x="3.4" y="3.4" width="17.2" height="17.2" rx="4.6" />
      <circle cx="12" cy="12" r="3.9" />
      <circle cx="16.9" cy="7.1" r="1.05" fill="currentColor" stroke="none" />
    </svg>
  ),
  Facebook: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.6-1.5H16.6V4.4A21 21 0 0 0 14.5 4.3c-2.2 0-3.7 1.3-3.7 3.9v2.3H8.2v3h2.6V21Z" />
    </svg>
  ),
  WhatsApp: (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.347.52-.52.174-.174.232-.298.347-.497.116-.198.058-.371-.03-.52-.087-.148-.66-1.59-.904-2.178-.24-.578-.482-.5-.66-.51-.174-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  ),
  Arrow: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" {...p}>
      <path d="M4.5 12h14m-5-5.2L18.8 12l-5.3 5.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Close: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...p}>
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  ),
  Chevron: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...p}>
      <path d="m9 5 7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Diamond: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" {...p}>
      <path d="M12 3 21 9.6 12 21 3 9.6 12 3Z" strokeLinejoin="round" />
      <path d="M3 9.6h18M8.4 9.6 12 21m3.6-11.4L12 21M8.4 9.6 12 3l3.6 6.6" strokeLinejoin="round" />
    </svg>
  ),
  Crown: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" {...p}>
      <path d="M3 18h18M4.2 8.4 7.5 11l2.3-4.8L12 9.7l2.2-3.5L16.5 11l3.3-2.6L18.4 18H5.6L4.2 8.4Z" strokeLinejoin="round" />
    </svg>
  ),
  Handshake: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" {...p}>
      <path d="m2.8 12.6 3.4-3.4 3 3 2.8-2.8 2.8 2.8 3-3 3.4 3.4M8 15.2l2.6 2.6a1.7 1.7 0 0 0 2.4 0l4.4-4.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Ring: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" {...p}>
      <circle cx="12" cy="14.6" r="6" />
      <path d="m9 6.4h6l-1.6 3.2h-2.8L9 6.4Z" strokeLinejoin="round" />
    </svg>
  ),
};
